import { useState, useEffect, useRef, useCallback } from "react";
import {
  LayoutDashboard, ShoppingCart, Store, MapPin, CheckCircle2, ShieldCheck,
  RefreshCw, Plus, LogOut, Menu, X, Bell, MapPinned, Clock, ArrowRight, Wallet, Tag,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { initials, rupiah, fmtDateTime } from "@/lib/format";
import Dashboard from "@/pages/Dashboard";
import SalesOrders from "@/pages/SalesOrders";
import Customers from "@/pages/Customers";
import Visits from "@/pages/Visits";
import Approval from "@/pages/Approval";
import Finance from "@/pages/Finance";
import Team from "@/pages/Team";
import OdooSync from "@/pages/OdooSync";
import DistributorCoverage from "@/pages/DistributorCoverage";

const MODULES = [
  { key: "ringkasan", label: "Ringkasan", icon: LayoutDashboard, title: "Ringkasan Penjualan", eyebrow: "DASHBOARD", perm: "dashboard:read" },
  { key: "sales", label: "Sales Order", icon: ShoppingCart, title: "Sales Order", eyebrow: "PENJUALAN", perm: "orders:read" },
  { key: "customers", label: "Customer & Produk", icon: Store, title: "Customer & Produk", eyebrow: "MASTER DATA", perms: ["customers:create", "customers:update", "pricelists:create", "customers:ar_term"] },
  { key: "kunjungan", label: "Kunjungan", icon: MapPin, title: "Kunjungan Sales", eyebrow: "LAPANGAN", perms: ["visits:read", "visits:create", "visits:update", "visits:manage"] },
  { key: "approval", label: "Approval", icon: CheckCircle2, title: "Approval", eyebrow: "PERSETUJUAN", perm: "approval:manage" },
  { key: "finance", label: "Finance", icon: Wallet, title: "Finance & Penagihan", eyebrow: "KEUANGAN", perm: "finance:read" },
  { key: "team", label: "Tim & Akses", icon: ShieldCheck, title: "Tim & Akses", eyebrow: "PENGGUNA", perm: "users:manage" },
  { key: "distributor", label: "Wilayah Distributor", icon: MapPinned, title: "Wilayah Distributor", eyebrow: "COVERAGE", perm: "distributor:manage" },
  { key: "odoo", label: "Sinkronisasi Odoo", icon: RefreshCw, title: "Sinkronisasi Odoo", eyebrow: "INTEGRASI", perms: ["odoo:sync", "odoo:connection"] },
];

const ENV_PILL = { production: "PRODUCTION", demo: "DEMO", off: "NONAKTIF" };
const KIND = { create: "baru", update: "diubah", delete: "dihapus" };

export default function Shell() {
  const { user, logout, hasPerm } = useAuth();
  const nav = MODULES.filter((m) => (!m.perm || hasPerm(m.perm)) && (!m.perms || m.perms.some((p) => hasPerm(p))));
  const [active, setActive] = useState(() => nav[0]?.key || "sales");
  const [open, setOpen] = useState(false);
  const [newOrderSignal, setNewOrderSignal] = useState(0);
  const [odooEnv, setOdooEnv] = useState("off");

  useEffect(() => {
    api.get("/health").then((r) => setOdooEnv(r.data.odoo_configured ? r.data.odoo_env : "off")).catch(() => {});
  }, [active]);

  const navKeys = nav.map((m) => m.key).join(",");
  const current = nav.find((m) => m.key === active) || MODULES.find((m) => m.key === active) || nav[0] || MODULES[0];

  useEffect(() => {
    if (nav.length && !nav.some((m) => m.key === active)) setActive(nav[0].key);
  }, [navKeys, active]);

  const goto = (k) => { setActive(k); setOpen(false); };

  const openNewOrder = () => { setActive("sales"); setNewOrderSignal((n) => n + 1); };
  const live = odooEnv !== "off";

  return (
    <div className="app-shell">
      {open && <button className="nav-scrim" onClick={() => setOpen(false)} aria-label="Tutup menu" />}
      <aside className={`sidebar ${open ? "sidebar-open" : ""}`}>
        <div className="brand">
          <div className="brand-logo-frame">
            <img src="/rasa-juara-logo-v3.webp?v=3" alt="Rasa Juara" className="brand-logo-image" />
          </div>
          <div className="brand-product">
            <b>SALES HUB</b>
            <span>RASA JUARA</span>
          </div>
          <button className="mobile-close" onClick={() => setOpen(false)}><X /></button>
        </div>
        <p className="workspace-label">WORKSPACE</p>
        <nav>
          {nav.map((m) => {
            const Icon = m.icon;
            return (
              <button key={m.key} className={active === m.key ? "active" : ""} onClick={() => goto(m.key)} data-testid={`nav-${m.key}`}>
                <Icon /> {m.label}
              </button>
            );
          })}
        </nav>
        <div className="sidebar-foot">
          <div className="mini-avatar">{initials(user?.name)}</div>
          <div>
            <strong>{user?.name}</strong>
            <span>{user?.role}</span>
          </div>
          <button onClick={logout} data-testid="logout-btn" title="Keluar" style={{ border: 0, background: "none", cursor: "pointer" }}>
            <LogOut />
          </button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <div className="page-title">
            <button className="menu-btn" onClick={() => setOpen(true)} aria-label="Menu" data-testid="mobile-menu-btn"><Menu size={18} /></button>
            <div>
              <span>{current.eyebrow}</span>
              <h1 data-testid="page-title">{current.title}</h1>
            </div>
          </div>
          <div className="top-actions">
            <span className={`rj-pill ${live ? "rj-pill-live" : "rj-pill-mock"} env-${odooEnv}`} data-testid="odoo-mode-pill" title={live ? `Terhubung ke Odoo ${ENV_PILL[odooEnv]}` : "Tidak ada koneksi Odoo aktif"}>
              Odoo: {ENV_PILL[odooEnv] || odooEnv}
            </span>
            <NotificationBell onGoto={goto} newOrderSignal={newOrderSignal} />
            {hasPerm("orders:create") && (
              <button className="rj-btn rj-btn-primary new-sale" onClick={openNewOrder} data-testid="new-sale-btn">
                <Plus size={18} /> Input penjualan
              </button>
            )}
          </div>
        </header>

        <main className="content">
          {active === "ringkasan" && <Dashboard goto={goto} />}
          {active === "sales" && <SalesOrders newOrderSignal={newOrderSignal} />}
          {active === "customers" && <Customers />}
          {active === "kunjungan" && <Visits />}
          {active === "approval" && <Approval />}
          {active === "finance" && <Finance />}
          {active === "team" && <Team />}
          {active === "distributor" && <DistributorCoverage />}
          {active === "odoo" && <OdooSync />}
        </main>
      </div>
    </div>
  );
}

function PriceNotifList({ items, onOpen }) {
  if (!items.length) return null;
  return (
    <>
      <header className="notif-subhead"><strong><Tag size={13} /> Perubahan harga</strong></header>
      <ul data-testid="notif-price-list">
        {items.map((n) => (
          <li key={n.id} className={n.read ? "" : "unread"} data-testid={`notif-price-${n.id}`} onClick={onOpen}>
            <div className="notif-item-head">
              <strong>{n.pricelist_name || "Pricelist"}</strong>
              <span className="notif-amt">{n.change_count} produk</span>
            </div>
            <div className="notif-price-changes">
              {n.changes.slice(0, 2).map((c, i) => (
                <span key={i}>{c.product_name}: {c.old_price != null ? rupiah(c.old_price) : "—"} → {c.new_price != null ? rupiah(c.new_price) : KIND[c.change_type]}</span>
              ))}
            </div>
            <div className="notif-item-sub">
              <span className="notif-so">{n.source === "odoo" ? "via Odoo" : "via Sales Hub"}{n.actor_name ? ` · ${n.actor_name}` : ""}</span>
              <span className="notif-time"><Clock size={11} /> {fmtDateTime(n.created_at)}</span>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

function NotificationBell({ onGoto, newOrderSignal }) {
  const { hasPerm } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ count: 0, items: [] });
  const [price, setPrice] = useState({ unread: 0, items: [] });
  const ref = useRef(null);
  const can = hasPerm("orders:read");

  const fetchNotif = useCallback(() => {
    if (!can) return;
    api.get("/sales/notifications").then((r) => setData(r.data.data)).catch(() => {});
    api.get("/notifications/price-changes").then((r) => setPrice(r.data.data)).catch(() => {});
  }, [can]);

  useEffect(() => {
    fetchNotif();
    const t = setInterval(fetchNotif, 60000);
    return () => clearInterval(t);
  }, [fetchNotif]);

  useEffect(() => { const t = setTimeout(fetchNotif, 1500); return () => clearTimeout(t); }, [newOrderSignal, fetchNotif]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!can) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    fetchNotif();
    if (next && price.unread > 0) {
      api.post("/notifications/price-changes/read").then(() => setPrice((p) => ({ ...p, unread: 0 }))).catch(() => {});
    }
  };
  const openSales = () => { onGoto("sales"); setOpen(false); };
  const openCustomers = () => { onGoto("customers"); setOpen(false); };
  const total = data.count + price.unread;

  return (
    <div className="notif-wrap" ref={ref}>
      <button className="icon-btn" aria-label="Notifikasi" data-testid="notif-bell-btn" onClick={toggle}>
        <Bell />
        {total > 0 && <b data-testid="notif-badge">{total > 99 ? "99+" : total}</b>}
      </button>
      {open && (
        <div className="notif-panel" data-testid="notif-panel">
          <header><strong>Order menunggu diproses</strong><span className="notif-count-pill">{data.count}</span></header>
          {data.items.length === 0 ? (
            <div className="notif-empty"><CheckCircle2 size={22} /><p>Semua order sudah diproses.</p></div>
          ) : (
            <ul>
              {data.items.map((o) => (
                <li key={o.id} data-testid={`notif-item-${o.so_number}`} onClick={openSales}>
                  <div className="notif-item-head">
                    <strong>{o.outlet}</strong>
                    <span className="notif-amt">{rupiah(o.amount)}</span>
                  </div>
                  <div className="notif-item-sub">
                    <span className="notif-so">{o.so_number}</span>
                    <span className="notif-time"><Clock size={11} /> {fmtDateTime(o.created_at)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <PriceNotifList items={price.items} onOpen={openCustomers} />
          <button className="notif-viewall" onClick={openSales} data-testid="notif-view-all">
            Lihat semua Sales Order <ArrowRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
